import { useEffect, useMemo, useState } from 'react'
import { RowWindow } from '@/components/RowWindow'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BookmarkPlus,
  Download,
  Pencil,
  Play,
  Plus,
  Star,
  Table2,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import {
  downloadFile,
  formatCell,
  type CatalogueDataset,
  type CatalogueField,
  type ReportDefinition,
  type ReportFilterInput,
  type ReportResult,
  type SavedReport,
  MAX_SAVED_REPORTS,
} from '@/lib/reports'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Select, Toggle } from '@/components/ui/primitives'
import { t } from '@/i18n'

const OPERATORS: { value: ReportFilterInput['operator']; label: string }[] = [
  { value: 'Equals', label: 'is' },
  { value: 'NotEquals', label: 'is not' },
  { value: 'Contains', label: 'contains' },
  { value: 'GreaterThan', label: 'is more than' },
  { value: 'LessThan', label: 'is less than' },
]

/**
 * Assembles a report from checkboxes.
 *
 * The whole idea rests on one distinction the user never has to learn the name of: a column that
 * describes a row groups the report, and a column that measures one gets totalled. Tick "Category"
 * and "Profit" and you get profit by category, without anyone having to think about GROUP BY.
 */
export function ReportBuilder({ defaultFrom, defaultTo }: { defaultFrom: string; defaultTo: string }) {
  const [datasetId, setDatasetId] = useState('SaleLines')
  const [columns, setColumns] = useState<string[]>(['day', 'net'])
  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(defaultTo)
  const [filters, setFilters] = useState<ReportFilterInput[]>([])
  const [detailRows, setDetailRows] = useState(false)
  const [result, setResult] = useState<ReportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  /** The template being saved or renamed, when either dialog is up. */
  const [saving, setSaving] = useState<{ id: number | null; name: string } | null>(null)
  /** Which template the current columns came from, so saving again updates it by name. */
  const [loadedName, setLoadedName] = useState<string | null>(null)

  const queryClient = useQueryClient()

  const catalogue = useQuery({
    queryKey: ['report-catalogue'],
    queryFn: () => apiClient.get<CatalogueDataset[]>('/api/reports/builder/catalogue'),
  })

  const dataset = catalogue.data?.find((entry) => entry.id === datasetId)

  // Grouped for the eye: "When", "Where", "Who", "What", then the numbers.
  const groups = useMemo(() => {
    const byGroup = new Map<string, CatalogueField[]>()
    for (const field of dataset?.fields ?? []) {
      const key = field.group ?? 'Other'
      byGroup.set(key, [...(byGroup.get(key) ?? []), field])
    }
    return [...byGroup.entries()]
  }, [dataset])

  /**
   * The columns in the order the report will actually use them.
   *
   * The server decides this — dimensions first, then measures, each in catalogue order — so that
   * the headings always describe the cells beneath them. Mirrored here only so the choice is
   * visible while it is being made, rather than rearranging itself when the report runs.
   */
  const chosen = useMemo(
    () => (dataset?.fields ?? []).filter((field) => columns.includes(field.id)),
    [dataset, columns],
  )

  const templates = useQuery({
    queryKey: ['saved-reports'],
    queryFn: () => apiClient.get<SavedReport[]>('/api/reports/builder/saved'),
  })

  const definition: ReportDefinition = {
    dataset: datasetId,
    columns,
    from: dataset?.isDated ? from : null,
    to: dataset?.isDated ? to : null,
    filters,
    detailRows,
  }

  const run = useMutation({
    mutationFn: () => apiClient.post<ReportResult>('/api/reports/builder/run', definition),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not run that.'),
  })

  async function refreshTemplates() {
    await queryClient.invalidateQueries({ queryKey: ['saved-reports'] })
  }

  const saveTemplate = useMutation({
    mutationFn: (name: string) =>
      apiClient.post<SavedReport>('/api/reports/builder/saved', { name, definition }),
    onSuccess: async (kept) => {
      setError(null)
      setSaving(null)
      setLoadedName(kept.name)
      await refreshTemplates()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save that.',
      ),
  })

  const renameTemplate = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      apiClient.put<SavedReport>(`/api/reports/builder/saved/${id}`, { name }),
    onSuccess: async (renamed) => {
      setError(null)
      setSaving(null)
      setLoadedName(renamed.name)
      await refreshTemplates()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not rename that.',
      ),
  })

  const deleteTemplate = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/reports/builder/saved/${id}`),
    onSuccess: refreshTemplates,
    onError: () => setError('Could not delete that.'),
  })

  /**
   * Loads a template back into the builder.
   *
   * Every choice on the screen is replaced, dates included: a template saved for a fixed period
   * should reopen on that period. One saved without dates falls back to the range the screen came
   * up with, rather than to nothing.
   */
  function load(template: SavedReport) {
    setDatasetId(template.definition.dataset)
    setColumns([...template.definition.columns])
    setFilters([...(template.definition.filters ?? [])])
    setDetailRows(template.definition.detailRows ?? false)
    setFrom(template.definition.from ?? defaultFrom)
    setTo(template.definition.to ?? defaultTo)
    setLoadedName(template.name)
    setResult(null)
    setError(null)
  }

  function toggle(id: string) {
    setColumns((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    )
  }

  const exportCsv = useMutation({
    mutationFn: () =>
      downloadFile('/api/reports/builder/export', `${datasetId.toLowerCase()}.csv`, definition),
    onError: () => setError('Could not export that.'),
  })

  const dimensionCount = (dataset?.fields ?? []).filter(
    (field) => columns.includes(field.id) && field.kind === 'Dimension',
  ).length
  const measureCount = (dataset?.fields ?? []).filter(
    (field) => columns.includes(field.id) && field.kind === 'Measure',
  ).length

  return (
    <div className="flex flex-col gap-5">
      {/* Templates: a report someone built once and wants back tomorrow. */}
      <section className="surface-raised rounded-[var(--radius-card)] border p-4">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-medium">{t('Your saved reports')}</p>
          <p className="text-xs text-[var(--text-secondary)]">
            {templates.data?.length ?? 0} of {MAX_SAVED_REPORTS}
          </p>
        </div>

        {templates.data?.length ? (
          <ul className="flex flex-wrap gap-2">
            {templates.data.map((template) => (
              <li
                key={template.id}
                className={`flex items-center gap-0.5 rounded-full border py-0.5 pl-1 pr-0.5 ${
                  loadedName === template.name
                    ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                    : 'surface-sunken'
                }`}
              >
                <button
                  type="button"
                  onClick={() => load(template)}
                  className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-sm font-medium"
                  title={`${template.datasetLabel} · ${template.definition.columns.length} columns`}
                >
                  <Star className="h-3.5 w-3.5" />
                  {template.name}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Rename ${template.name}`}
                  onClick={() => setSaving({ id: template.id, name: template.name })}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${template.name}`}
                  onClick={() => deleteTemplate.mutate(template.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-[var(--text-secondary)]">
            Build a report below, then keep it here to open it again with one tap. A template holds
            the question, never the answer — it runs afresh every time.
          </p>
        )}
      </section>

      {/* What the report is about */}
      <section className="surface-raised rounded-[var(--radius-card)] border p-4">
        <p className="mb-2 text-sm font-medium">{t('What are you reporting on?')}</p>
        <div className="flex flex-wrap gap-2">
          {catalogue.data?.map((entry) => (
            <Button
              key={entry.id}
              variant={entry.id === datasetId ? 'primary' : 'secondary'}
              size="sm"
              className="touch-target"
              onClick={() => {
                setDatasetId(entry.id)
                // Columns belong to a dataset, so they cannot survive a change of subject.
                setColumns([])
                setFilters([])
                setResult(null)
              }}
            >
              {entry.label}
            </Button>
          ))}
        </div>
        {dataset && (
          <p className="mt-2 text-xs text-[var(--text-secondary)]">{dataset.explanation}</p>
        )}
      </section>

      {/* Columns */}
      <section className="surface-raised rounded-[var(--radius-card)] border p-4">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">{t('Which columns?')}</p>
            {/* Twenty-odd boxes is a lot of tapping to answer "everything" or to start again. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setColumns((dataset?.fields ?? []).map((field) => field.id))}
              disabled={dataset === undefined || columns.length === (dataset?.fields.length ?? 0)}
            >
              {t('Select all')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setColumns([])}
              disabled={columns.length === 0}
            >
              {t('Select none')}
            </Button>
          </div>
          <p className="text-xs text-[var(--text-secondary)]">
            {measureCount === 0
              ? 'Tick a number to total it'
              : dimensionCount === 0
                ? 'One row, totalling everything'
                : `Grouped by ${dimensionCount} column${dimensionCount === 1 ? '' : 's'}`}
          </p>
        </div>

        {/* The order the report will come out in, shown while choosing rather than sprung at the
            end. Ticking picks which columns appear, never where they go. */}
        {chosen.length > 0 && (
          <div className="surface-sunken mb-4 rounded-lg p-3">
            <p className="mb-2 text-xs text-[var(--text-secondary)]">
              {t('Your report, left to right')}
            </p>
            <ol className="flex flex-wrap items-center gap-1.5">
              {chosen.map((field, index) => (
                <li key={field.id} className="flex items-center gap-1.5">
                  {index > 0 && <span className="text-[var(--text-secondary)]">›</span>}
                  <button
                    type="button"
                    onClick={() => toggle(field.id)}
                    aria-label={`Remove ${field.label}`}
                    className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                      field.kind === 'Measure'
                        ? 'bg-[var(--accent)] text-[var(--accent-contrast)]'
                        : 'surface-raised border'
                    }`}
                  >
                    {field.label}
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map(([group, fields]) => (
            <div key={group}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                {group}
              </p>
              <ul className="flex flex-col gap-1">
                {fields.map((field) => (
                  <li key={field.id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1.5 text-sm hover:bg-[var(--surface-sunken)]">
                      <input
                        type="checkbox"
                        checked={columns.includes(field.id)}
                        onChange={() => toggle(field.id)}
                        className="h-4 w-4 shrink-0 accent-[var(--accent)]"
                      />
                      <span className="min-w-0 flex-1 truncate">{field.label}</span>
                      {/* Says which columns will be added up, without using the word "measure". */}
                      {field.kind === 'Measure' && (
                        <Badge tone="neutral">
                          {field.aggregate === 'Sum' ? 'total' : field.aggregate.toLowerCase()}
                        </Badge>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Filters and period */}
      <section className="surface-raised rounded-[var(--radius-card)] border p-4">
        <p className="mb-3 text-sm font-medium">{t('Narrow it down')}</p>

        {dataset?.isDated ? (
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <Input label={t('From')} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t('To')} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        ) : (
          <p className="mb-3 text-xs text-[var(--text-secondary)]">
            {/* Three datasets have no dates and each is a snapshot for its own reason, so the
                sentence says which one rather than always talking about the shelf. */}
            {datasetId === "StockOnHand"
              ? "Stock is a snapshot of what is on the shelf now, so a date range does not apply."
              : "This is how things stand right now, not a record of a period, so a date range does not apply."}
          </p>
        )}

        <ul className="flex flex-col gap-2">
          {filters.map((filter, index) => (
            <li key={index} className="flex flex-wrap items-end gap-2">
              <Select
                label={t('Column')}
                value={filter.fieldId}
                wrapperClassName="min-w-40 flex-1"
                onChange={(e) =>
                  setFilters((current) =>
                    current.map((entry, position) =>
                      position === index ? { ...entry, fieldId: e.target.value } : entry,
                    ),
                  )
                }
              >
                {dataset?.fields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </Select>
              <Select
                label={t('Test')}
                value={filter.operator}
                wrapperClassName="min-w-32"
                onChange={(e) =>
                  setFilters((current) =>
                    current.map((entry, position) =>
                      position === index
                        ? { ...entry, operator: e.target.value as ReportFilterInput['operator'] }
                        : entry,
                    ),
                  )
                }
              >
                {OPERATORS.map((operator) => (
                  <option key={operator.value} value={operator.value}>
                    {operator.label}
                  </option>
                ))}
              </Select>
              <Input
                label={t('Value')}
                value={filter.value}
                wrapperClassName="min-w-32 flex-1"
                onChange={(e) =>
                  setFilters((current) =>
                    current.map((entry, position) =>
                      position === index ? { ...entry, value: e.target.value } : entry,
                    ),
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('Remove filter')}
                onClick={() => setFilters((current) => current.filter((_, p) => p !== index))}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setFilters((current) => [
                ...current,
                { fieldId: dataset?.fields[0]?.id ?? '', operator: 'Contains', value: '' },
              ])
            }
          >
            <Plus className="h-4 w-4" />
            {t('Add a filter')}
          </Button>

          <Toggle
            checked={detailRows}
            onChange={setDetailRows}
            label={t('Every row, not totals')}
            hint={t('Lists the underlying records instead of grouping them')}
          />
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => run.mutate()} disabled={columns.length === 0 || run.isPending}>
          <Play className="h-4 w-4" />
          {run.isPending ? 'Running…' : 'Run report'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => exportCsv.mutate()}
          disabled={columns.length === 0 || exportCsv.isPending}
        >
          <Download className="h-4 w-4" />
          {exportCsv.isPending ? 'Exporting…' : 'Export CSV'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => setSaving({ id: null, name: loadedName ?? '' })}
          disabled={columns.length === 0}
        >
          <BookmarkPlus className="h-4 w-4" />
          {loadedName ? 'Save changes' : 'Save as template'}
        </Button>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          <TriangleAlert className="mr-1 inline h-4 w-4" />
          {error}
        </p>
      )}

      {result && <ResultTable result={result} />}

      <NameTemplateDialog
        open={saving !== null}
        initial={saving?.name ?? ''}
        renaming={saving?.id != null}
        busy={saveTemplate.isPending || renameTemplate.isPending}
        onClose={() => setSaving(null)}
        onSubmit={(name) =>
          saving?.id == null
            ? saveTemplate.mutate(name)
            : renameTemplate.mutate({ id: saving.id, name })
        }
      />
    </div>
  )
}

/**
 * Names a template on the way in.
 *
 * Saving over a name you already hold replaces it rather than adding a second button with the
 * same label — that is the server's rule, so the field is prefilled with whatever the report on
 * screen was loaded from, and pressing save again is how a template gets corrected.
 */
function NameTemplateDialog({
  open,
  initial,
  renaming,
  busy,
  onClose,
  onSubmit,
}: {
  open: boolean
  initial: string
  renaming: boolean
  busy: boolean
  onClose: () => void
  onSubmit: (name: string) => void
}) {
  const [name, setName] = useState(initial)

  useEffect(() => {
    if (open) setName(initial)
  }, [open, initial])

  const trimmed = name.trim()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={renaming ? 'Rename this report' : 'Save this report'}
      description={
        renaming
          ? undefined
          : 'It reopens with these columns and filters, and runs again against whatever the numbers are then.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => onSubmit(trimmed)} disabled={trimmed.length === 0 || busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <Input
        label={t('Name')}
        value={name}
        maxLength={60}
        autoFocus
        placeholder={t('e.g. Margin by category')}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && trimmed.length > 0 && !busy) onSubmit(trimmed)
        }}
      />
    </Modal>
  )
}

function ResultTable({ result }: { result: ReportResult }) {
  if (result.rows.length === 0) {
    return (
      <EmptyState
        icon={Table2}
        title={t('Nothing matched')}
        description={t('No records fell inside that period and those filters.')}
      />
    )
  }

  return (
    <section className="surface-raised rounded-[var(--radius-card)] border">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--border-subtle)] p-3">
        <p className="text-sm font-medium">
          {result.rowCount.toLocaleString()} {result.rowCount === 1 ? 'row' : 'rows'}
        </p>
        <p className="text-xs text-[var(--text-secondary)]">
          from {result.sourceRowCount.toLocaleString()} records · days cut on {result.timeZone}
        </p>
      </div>

      {/* The totals under the table are computed from every row, including the ones that did not
          come back — so without this the figures on screen visibly fail to add up to the total
          beneath them, with nothing saying why. */}
      {result.truncated && (
        <p className="border-b border-[var(--border-subtle)] bg-warn-500/10 px-3 py-2 text-sm">
          {t('Too many rows to list. These are the first {count} — the totals below still cover every record. Narrow the dates or group by fewer columns.', { count: result.rowCount.toLocaleString() })}
        </p>
      )}

      {/* Scrolls inside itself: a wide report must never make the page scroll sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border-subtle)]">
              {result.columns.map((column) => (
                <th
                  key={column.id}
                  className={`whitespace-nowrap px-3 py-2 font-medium ${
                    column.kind === 'Measure' ? 'text-right' : 'text-left'
                  }`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <RowWindow rows={result.rows} colSpan={result.columns.length}>{(row, index) => (
              <tr key={index} className="border-b border-[var(--border-subtle)] last:border-0">
                {row.map((cell, position) => (
                  <td
                    key={position}
                    className={`whitespace-nowrap px-3 py-1.5 ${
                      result.columns[position]?.kind === 'Measure' ? 'tabular text-right' : ''
                    }`}
                  >
                    {formatCell(cell, result.columns[position]?.valueType ?? 'Text')}
                  </td>
                ))}
              </tr>
            )}</RowWindow>
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--border-subtle)] font-semibold">
              {result.totals.map((cell, position) => (
                <td
                  key={position}
                  className={`whitespace-nowrap px-3 py-2 ${
                    result.columns[position]?.kind === 'Measure' ? 'tabular text-right' : ''
                  }`}
                >
                  {cell === null
                    ? position === 0
                      ? 'Total'
                      : ''
                    : formatCell(cell, result.columns[position]?.valueType ?? 'Text')}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}
